import React, { useRef, useState, useCallback } from "react";

import { Box } from "@mui/material";
import Draggable, { DraggableData, DraggableEvent } from "react-draggable";

import { GifOverlay as GifOverlayType } from "../../types";

interface GifOverlayProps {
  overlay: GifOverlayType;
  containerWidth: number;
  containerHeight: number;
  currentTimeMs: number;
  onUpdate: (id: string, updates: Partial<GifOverlayType>) => void;
}

export default function GifOverlay({
  overlay,
  containerWidth,
  containerHeight,
  currentTimeMs,
  onUpdate,
}: GifOverlayProps) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);

  const isVisible =
    currentTimeMs >= overlay.startMs && currentTimeMs <= overlay.endMs;

  const pxX = (overlay.x / 100) * containerWidth;
  const pxY = (overlay.y / 100) * containerHeight;
  const pxWidth = (overlay.width / 100) * containerWidth;

  const handleDragStop = useCallback(
    (_e: DraggableEvent, data: DraggableData) => {
      const newX = (data.x / containerWidth) * 100;
      const newY = (data.y / containerHeight) * 100;
      onUpdate(overlay.id, { x: newX, y: newY });
    },
    [containerWidth, containerHeight, overlay.id, onUpdate],
  );

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setIsResizing(true);

      const startX = e.clientX;
      const startW = pxWidth;

      const onMouseMove = (ev: MouseEvent) => {
        const newW = Math.max(30, startW + (ev.clientX - startX));
        onUpdate(overlay.id, {
          width: (newW / containerWidth) * 100,
        });
      };

      const onMouseUp = () => {
        setIsResizing(false);
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
      };

      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },
    [pxWidth, containerWidth, overlay.id, onUpdate],
  );

  if (!isVisible) return null;

  return (
    <Draggable
      nodeRef={nodeRef}
      position={{ x: pxX, y: pxY }}
      onStop={handleDragStop}
      disabled={isResizing}
      bounds="parent"
    >
      <Box
        ref={nodeRef}
        sx={{
          position: "absolute",
          width: pxWidth,
          cursor: "move",
          zIndex: 10,
          "&:hover .resize-handle": {
            opacity: 1,
          },
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={overlay.url}
          alt="GIF overlay"
          draggable={false}
          style={{
            display: "block",
            width: "100%",
            height: "auto",
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
        {/* Resize handle — width only, matching backend export behavior */}
        <Box
          className="resize-handle"
          onMouseDown={handleResizeMouseDown}
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            width: 14,
            height: 14,
            cursor: "ew-resize",
            background: "rgba(255,255,255,0.8)",
            borderRadius: "2px 0 0 0",
            opacity: 0,
            transition: "opacity 0.2s",
            "&:hover": {
              background: "rgba(100,150,255,0.9)",
            },
          }}
        />
      </Box>
    </Draggable>
  );
}
